import { FakeFiller } from '@/components/fake-filler'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AnimatePresence } from 'framer-motion'
import { Check, LoaderCircle, X } from 'lucide-react'
import { useRef, type BaseSyntheticEvent } from 'react'

const AddForm = ({
  addOpen,
  setAddOpen,
  onSubmit,
  update = false,
  isSubmitting = false,
  title = '',
  headerExtra,
  onHandleClose,
  fakeFiller,
  children,
  className,
  hideButtons = false,
  modalMode = false,
  submitButtonLabel = 'Submit',
  submitButtonIcon = <Check />,
  submittingLabel = 'Submitting',
  updatingLabel = 'Updating',
  cancelButtonIcon = <X />,
  cancelButtonLabel = 'Cancel',
  cancelButtonVariant = 'destructive',
  updateButtonLabel = 'Update',
  disableSubmit = false,
  disableCancel = false,
  stickyActions = true,
  formId,
}: {
  addOpen: boolean
  setAddOpen: (value: boolean) => void
  onSubmit?: (e?: BaseSyntheticEvent<object, unknown, unknown> | undefined) => Promise<void>
  update?: boolean
  isSubmitting?: boolean
  title?: string
  headerExtra?: React.ReactNode
  onHandleClose?: () => void
  fakeFiller?: () => void
  children?: React.ReactNode
  className?: string
  hideButtons?: boolean
  modalMode?: boolean
  submitButtonLabel?: string
  submitButtonIcon?: React.ReactNode
  submittingLabel?: string
  cancelButtonIcon?: React.ReactNode
  cancelButtonLabel?: string
  cancelButtonVariant?: 'default' | 'destructive' | 'outline'
  updateButtonLabel?: string
  updatingLabel?: string
  disableSubmit?: boolean
  disableCancel?: boolean
  /** Pin the Cancel/Submit bar to the bottom of the scroll area on long forms. */
  stickyActions?: boolean
  /** Lets a submit button outside the form (e.g. an onboarding footer) target it via `form={formId}`. */
  formId?: string
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scrollToFirstError = () => {
    const root = scrollContainerRef.current
    if (!root) return

    // The real scroll container may be a child inside `children` (e.g. an
    // `overflow-y-auto` panel), not the wrapper the ref sits on. Walk up from the
    // errored field to find the nearest actually-scrollable ancestor.
    const getScrollParent = (el: HTMLElement): HTMLElement | null => {
      let node: HTMLElement | null = el.parentElement
      while (node) {
        const overflowY = getComputedStyle(node).overflowY
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node
        node = node.parentElement
      }
      return null
    }

    // Validation is async, so the error DOM may not be painted yet - retry a few frames.
    let attempts = 0
    const tryScroll = () => {
      const firstError = root.querySelector<HTMLElement>('.animate-fade-in-error')
      if (firstError) {
        const container = getScrollParent(firstError) ?? root
        const offset =
          firstError.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          container.clientHeight / 2
        container.scrollTo({ top: Math.max(offset, 0), behavior: 'smooth' })
        return
      }
      if (attempts++ < 10) requestAnimationFrame(tryScroll)
    }
    requestAnimationFrame(tryScroll)
  }

  const handleCancel = () => {
    onHandleClose?.()
    setAddOpen(false)
  }

  const handleModalSubmit = () => {
    onSubmit?.()
    void scrollToFirstError()
  }

  // Modal mode - render without form wrapper and AnimatePresence
  if (modalMode)
    return (
      <div ref={scrollContainerRef} className={cn(`${className}`)} data-form-shortcuts>
        <div>
          {fakeFiller && (
            <div>
              <FakeFiller
                onClick={() => {
                  fakeFiller?.()
                }}
              />
            </div>
          )}
          {children}

          {/* Form actions */}
          {hideButtons ? null : (
            <div
              className={cn(
                'pt-4 mt-8 border-t flex justify-end space-x-3',
                stickyActions && 'sticky -bottom-2 z-20 pb-2 bg-white dark:bg-card'
              )}
            >
              <Button
                type='button'
                data-form-cancel
                onClick={handleCancel}
                variant={cancelButtonVariant}
                disabled={disableCancel}
              >
                {cancelButtonIcon}
                {cancelButtonLabel}
              </Button>
              <Button type='button' data-form-submit disabled={isSubmitting} onClick={handleModalSubmit}>
                {isSubmitting ? (
                  <>
                    <LoaderCircle className='animate-spin' />
                    {update ? updatingLabel : submittingLabel}&nbsp;...
                  </>
                ) : (
                  <div className='flex items-center gap-2 '>
                    {submitButtonIcon}
                    {update ? updateButtonLabel : submitButtonLabel}&nbsp;
                  </div>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    )

  // Default mode - render with form wrapper and AnimatePresence
  return (
    <AnimatePresence>
      {addOpen && (
        <div ref={scrollContainerRef} className={cn(`mb-4  border bg-white dark:bg-card p-4 rounded-sm ${className}`)}>
          <form
            id={formId}
            onSubmit={(e) => {
              e?.preventDefault()
              onSubmit?.(e)
              void scrollToFirstError()
            }}
          >
            <div>
              {title ? (
                <div className='mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-2'>
                  <h3 className='text-md font-medium'>{title}</h3>
                  {headerExtra}
                </div>
              ) : null}
              {fakeFiller && (
                <div>
                  <FakeFiller
                    onClick={() => {
                      fakeFiller?.()
                    }}
                  />
                </div>
              )}
              {children}

              {/* Form actions moved inside the form content */}
              {hideButtons ? null : (
                <div
                  className={cn(
                    'pt-4 mt-8 border-t flex justify-end space-x-3',
                    stickyActions && 'sticky -bottom-4 z-20 -mx-4 -mb-4 px-4 pb-4 rounded-b-sm bg-white dark:bg-card'
                  )}
                >
                  <Button
                    type='button'
                    data-form-cancel
                    disabled={disableCancel}
                    onClick={handleCancel}
                    variant={cancelButtonVariant}
                  >
                    {cancelButtonIcon}
                    {cancelButtonLabel}
                  </Button>
                  <Button type='submit' disabled={isSubmitting || disableSubmit}>
                    {isSubmitting ? (
                      <>
                        <LoaderCircle className='animate-spin' />
                        {update ? updatingLabel : submittingLabel}&nbsp;...
                      </>
                    ) : (
                      <div className='flex items-center gap-2 '>
                        {submitButtonIcon}
                        {update ? updateButtonLabel : submitButtonLabel}&nbsp;
                      </div>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </form>
        </div>
      )}
    </AnimatePresence>
  )
}

export default AddForm
